import { SupportedLanguage } from '@/constants';
import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { fetchAllFromCache, FetchAPIOptions } from './api';
import { fetchAllFromWordpressAndCache } from './wordpress-api';
import { getItems, Item } from './items';

export interface MediaItem {
  id: string;
  title: string;
  mediaItemId: string;
  mediaItemUrl: string;
  parentId: string;
  altText: string | null;
  mediaDetails: {
    file: string;
    meta: {
      title: string;
    };
  };
}

export interface CombinedItem extends Item {
  postMediaItems: MediaItem[];
}

const GET_MEDIA_ITEMS_QUERY = `
query get_media_items($parentIds: [ID]!, $postCount: Int!, $after: String) {
  mediaItems(first: $postCount, where: {parentIn: $parentIds}, after: $after) {
    pageInfo {
      hasNextPage
      endCursor
    }
    nodes {
      id
      title
      mediaItemId
      mediaItemUrl
      parentId
      altText
      mediaDetails {
        file
        meta {
          title
        }
      }
    }
  }
}
`;

/**
 * WordPress에서 미디어 아이템을 가져와 S3에 캐시 (refresh용)
 */
const fetchMediaItemsFromWordpress = async (
  parentIds: string[],
  label: string,
) => {
  return fetchAllFromWordpressAndCache(
    {
      query: GET_MEDIA_ITEMS_QUERY,
      getVariables: cursor => ({
        parentIds,
        postCount: 100,
        after: cursor,
      }),
      getPageInfo: data => ({
        hasNextPage: data?.mediaItems?.pageInfo?.hasNextPage || false,
        endCursor: data?.mediaItems?.pageInfo?.endCursor || null,
      }),
      getItems: data => data?.mediaItems?.nodes || [],
    },
    { label },
  );
};

/**
 * S3 캐시에서 미디어 아이템을 가져옴 (빌드/런타임용)
 */
const fetchMediaItemsFromCache = async (label: string) => {
  return fetchAllFromCache(
    {
      getPageInfo: data => ({
        hasNextPage: data?.mediaItems?.pageInfo?.hasNextPage || false,
      }),
      getItems: data => data?.mediaItems?.nodes || [],
      postCount: 100,
    },
    { label },
  );
};

export const getMediaItems = async (
  parentIds: string[],
  options: FetchAPIOptions<true>,
): Promise<MediaItem[]> => {
  const label = `${options?.label ? options.label + '-' : ''}fetchMediaItems`;
  const refresh = options?.refresh ?? false;

  const data = refresh
    ? await fetchMediaItemsFromWordpress(parentIds, label)
    : await fetchMediaItemsFromCache(label);

  return (
    data?.reverse().map((node: any) => {
      return {
        id: node.id,
        title: node.title,
        mediaItemId: node.mediaItemId,
        mediaItemUrl: node.mediaItemUrl,
        parentId: node.parentId,
        altText: node.altText ?? '',
        mediaDetails: {
          file: node.mediaDetails.file,
          meta: {
            title: node.mediaDetails.meta?.title,
          },
        },
      };
    }) || []
  );
};

export const getIrItems = async (
  locale?: SupportedLanguage,
  options?: FetchAPIOptions,
): Promise<CombinedItem[]> => {
  const irItems: Item[] = await getItems(CONTENT_CATEGORY_ID.ir, {
    refresh: options?.refresh ?? false,
    label: 'getIrItems-items',
  });

  const mediaItems: MediaItem[] = await getMediaItems(
    irItems.map(post => post.id),
    { refresh: options?.refresh ?? false, label: 'getIrItems-mediaItems' },
  );

  const data: CombinedItem[] = irItems
    .filter(post => {
      return locale ? post.lang?.includes(locale) : true;
    })
    .map(post => {
      const postMediaItems = mediaItems.filter(
        mediaItem => mediaItem.parentId === post.id,
      );
      return { ...post, postMediaItems };
    })
    // 첨부 없는 글은 숨긴다 — 미디어 라이브러리 기존 파일을 재사용하면 parentId 가 다른 글을
    // 가리켜 첨부 0건이 되고, 목록 컴포넌트가 postMediaItems[0] 에서 터져 페이지가 stale 로 남는다
    .filter(post => post.postMediaItems.length > 0);

  return data;
};

export const getNoticeItems = async (
  locale: SupportedLanguage,
  options?: FetchAPIOptions,
): Promise<CombinedItem[]> => {
  // Notice와 IR은 같은 데이터 사용. 페이지만 다르게 처리
  return getIrItems(locale, options);
};

export const getResourceItems = async (
  locale?: SupportedLanguage,
  options?: FetchAPIOptions,
): Promise<CombinedItem[]> => {
  const resourceItems: Item[] = await getItems(CONTENT_CATEGORY_ID.resources, {
    refresh: options?.refresh ?? false,
    label: 'getResourceItems-items',
  });
  const mediaItems: MediaItem[] = await getMediaItems(
    resourceItems.map(post => post.id),
    {
      refresh: options?.refresh ?? false,
      label: 'getResourceItems-mediaItems',
    },
  );

  const data: CombinedItem[] = resourceItems
    .filter(post => {
      return locale ? post.lang?.includes(locale) : true;
    })
    .map(post => {
      const postMediaItems = mediaItems.filter(
        mediaItem =>
          mediaItem.parentId === post.id &&
          post.content.includes(mediaItem.mediaItemUrl),
      );
      return { ...post, postMediaItems };
    });

  return data;
};
